<template>
  <div class="p-6 dark:text-gray-50" v-if="canAddAlloy || user?.is_staff">
    <div class="flex space-x-3 mb-6">
      <h1 class="text-2xl font-semibold dark:text-white">Add New Observability Configuration</h1>
    </div>

    <form @submit.prevent="submitForm" class="space-y-4 bg-white/80 dark:bg-gray-800/70 backdrop-blur-md dark:text-bg-gray dark:border-gray-800 p-6 rounded-lg shadow-sm border border-gray-200">

      <!-- Link Type Selection -->
      <div class="flex flex-col">
        <label class="mb-1 font-medium dark:text-white">
          Link To<span class="pl-1 text-red-500">*</span>
        </label>
        <div class="flex space-x-4">
          <label class="inline-flex items-center">
            <input
              type="radio"
              v-model="linkType"
              value="probe"
              class="form-radio h-4 w-4 text-blue-600"
              @change="handleLinkTypeChange"
            />
            <span class="ml-2">Probe</span>
          </label>
          <label class="inline-flex items-center">
            <input
              type="radio"
              v-model="linkType"
              value="site"
              class="form-radio h-4 w-4 text-blue-600"
              @change="handleLinkTypeChange"
            />
            <span class="ml-2">Site</span>
          </label>
        </div>
        <FieldError :message="fieldErrors.link_type" />
      </div>

      <!-- Probe Selection (when linkType is 'probe') -->
      <div v-if="linkType === 'probe'" class="flex flex-col">
        <label for="probe-search" class="mb-1 font-medium dark:text-white">
          Probe<span class="pl-1 text-red-500">*</span>
        </label>
        <div class="relative">
          <input
            id="probe-search"
            v-model="probeSearch"
            type="text"
            :placeholder="getSelectedProbeName || 'Search probes...'"
            class="w-full border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2"
            @focus="handleProbeFocus"
            @blur="handleProbeBlur"
            :class="[
              fieldErrors.probe
                ? 'ring-2 ring-red-500 focus:ring-red-500'
                : 'focus:ring-blue-500'
            ]"
          />
          <FieldError :message="fieldErrors.probe" />
          <div
            v-show="isProbeDropdownVisible"
            class="absolute w-full mt-1 max-h-60 overflow-y-auto bg-white dark:bg-gray-600 dark:text-gray-50 border border-gray-300 rounded shadow-lg z-10"
          >
            <div
              v-for="probe in filteredProbes"
              :key="probe.id"
              class="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
              @mousedown="selectProbe(probe)"
            >
              {{ probe.name }}
            </div>
            <div
              v-if="filteredProbes.length === 0"
              class="px-3 py-2 text-gray-500"
            >
              No probes found
            </div>
          </div>
        </div>
      </div>

      <!-- Site Selection (when linkType is 'site') -->
      <div v-if="linkType === 'site'" class="flex flex-col">
        <label for="site-search" class="mb-1 font-medium dark:text-white">
          Site<span class="pl-1 text-red-500">*</span>
        </label>
        <div class="relative">
          <input
            id="site-search"
            v-model="siteSearch"
            type="text"
            :placeholder="getSelectedSiteName || 'Search sites...'"
            class="w-full border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2"
            @focus="handleSiteFocus"
            @blur="handleSiteBlur"
            :class="[
              fieldErrors.site
                ? 'ring-2 ring-red-500 focus:ring-red-500'
                : 'focus:ring-blue-500'
            ]"
          />
          <FieldError :message="fieldErrors.site" />
          <div
            v-show="isSiteDropdownVisible"
            class="absolute w-full mt-1 max-h-60 overflow-y-auto bg-white dark:bg-gray-600 dark:text-gray-50 border border-gray-300 rounded shadow-lg z-10"
          >
            <div
              v-for="site in filteredSites"
              :key="site.id"
              class="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
              @mousedown="selectSite(site)"
            >
              {{ site.name }}
            </div>
            <div
              v-if="filteredSites.length === 0"
              class="px-3 py-2 text-gray-500"
            >
              No sites found
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex justify-end space-x-2">
        <button
          type="button"
          @click="$router.push({ name: 'AlloyConfigs' })"
          class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
        >
          Create Observability Config
        </button>
      </div>
    </form>
  </div>

  <!-- Error Message -->
  <PageError
    v-if="permsLoaded && !canAddAlloy && !user?.is_staff"
    :error="errorMessages['403']"
    :errorCode="'403'"
  />
</template>

<script>
import { createAlloyConfig } from "@/services/alloyService";
import { getAllProbes } from "@/services/probeService";
import { getSites } from "@/services/siteService";
import FieldError from "@/components/FieldError.vue";
import PageError from "@/components/PageError.vue";
import { useStore } from "@/store";
import { errorMessages } from "@/utils/errorMessages";
import { useMultiTenant } from "@/composables/useMultiTenant";

export default {
  name: "AddAlloy",
  components: {
    FieldError,
    PageError,
  },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      errorMessages: errorMessages,
      fieldErrors: {},
      linkType: "probe",
      formData: {
        probe: null,
        site: null,
      },
      probes: [],
      sites: [],
      probeSearch: "",
      siteSearch: "",
      isProbeDropdownVisible: false,
      isSiteDropdownVisible: false,
      canAddAlloy: false,
    };
  },
  computed: {
    user() {
      return useStore.getUser();
    },
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
    filteredProbes() {
      if (!this.probeSearch) return this.probes;
      return this.probes.filter(
        (probe) =>
          probe.name.toLowerCase().includes(this.probeSearch.toLowerCase()) ||
          probe.id.toString().includes(this.probeSearch)
      );
    },
    filteredSites() {
      if (!this.siteSearch) return this.sites;
      return this.sites.filter(
        (site) =>
          site.name.toLowerCase().includes(this.siteSearch.toLowerCase()) ||
          site.id.toString().includes(this.siteSearch)
      );
    },
    getSelectedProbeName() {
      if (!this.formData.probe) return null;
      const selectedProbe = this.probes.find((p) => p.id === this.formData.probe);
      return selectedProbe ? selectedProbe.name : null;
    },
    getSelectedSiteName() {
      if (!this.formData.site) return null;
      const selectedSite = this.sites.find((s) => s.id === this.formData.site);
      return selectedSite ? selectedSite.name : null;
    },
  },
  async created() {
    try {
      await this.tenant.fetchOrganizations();
      await this.tenant.loadInitialData(this.fetchData);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  },
  watch: {
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true,
    },
  },
  methods: {
    async fetchData(schemaName = null) {
      const [probes, sites] = await Promise.all([
        getAllProbes(schemaName),
        getSites(schemaName),
      ]);
      this.probes = probes;
      this.sites = sites;
    },
    async checkPermissions() {
      if (this.user) {
        this.canAddAlloy = await useStore.hasPermission("Can add Alloy Config");
      }
    },
    handleLinkTypeChange() {
      // Clear selections when switching link type
      this.formData.probe = null;
      this.formData.site = null;
      this.probeSearch = "";
      this.siteSearch = "";
      this.fieldErrors = {};
    },
    handleProbeFocus() {
      this.isProbeDropdownVisible = true;
      this.probeSearch = "";
    },
    handleProbeBlur() {
      setTimeout(() => {
        this.isProbeDropdownVisible = false;
      }, 200);
    },
    handleSiteFocus() {
      this.isSiteDropdownVisible = true;
      this.siteSearch = "";
    },
    handleSiteBlur() {
      setTimeout(() => {
        this.isSiteDropdownVisible = false;
      }, 200);
    },
    selectProbe(probe) {
      this.formData.probe = probe.id;
      this.isProbeDropdownVisible = false;
      this.probeSearch = "";
    },
    selectSite(site) {
      this.formData.site = site.id;
      this.isSiteDropdownVisible = false;
      this.siteSearch = "";
    },
    async submitForm() {
      this.fieldErrors = {};

      // Validate link selection
      if (this.linkType === "probe" && !this.formData.probe) {
        this.fieldErrors.probe = "Please select a probe";
        return;
      }
      if (this.linkType === "site" && !this.formData.site) {
        this.fieldErrors.site = "Please select a site";
        return;
      }

      try {
        const configData = {};

        if (this.linkType === "probe") {
          configData.probe = this.formData.probe;
        } else {
          configData.site = this.formData.site;
        }

        const newConfig = await createAlloyConfig(configData, this.tenant.currentSchemaName.value);
        this.$router.push({
          name: "AlloyDetails",
          params: { id: newConfig.id },
          query: this.tenant.addSchemaParam(),
        });
      } catch (error) {
        const errData = error?.response?.data;
        if (errData) {
          Object.keys(errData).forEach((key) => {
            if (Array.isArray(errData[key])) {
              this.fieldErrors[key] = errData[key][0];
            } else {
              this.fieldErrors[key] = errData[key];
            }
          });
        }
        console.error("Error creating alloy config:", error);
      }
    },
  },
};
</script>
